import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/sharp-light-svg-icons";
import {
    faClockRotateLeft,
    faSpinner,
} from "@fortawesome/sharp-regular-svg-icons";
import { Disclosure } from "@headlessui/react";
import { useQuery } from "@tanstack/react-query";
import type {
    FieldChange,
    HistoryEntry,
} from "../../../../apiClient/generated";
import { useEmissionEventsApiClient } from "../../../../hooks";
import { formatDateTime } from "../../../../utils/dates";

const DEFAULT_USER_NAME = "AERSHED";

const AVATAR_COLORS = [
    "bg-ae-blue-500",
    "bg-ae-blue-600",
    "bg-ae-blue-900",
    "bg-ae-emission-3rd-party",
    "bg-ae-emission-self-reported",
    "bg-ae-emission-epa-sep",
];

function avatarColor(initials: string) {
    return AVATAR_COLORS[initials.charCodeAt(0) % AVATAR_COLORS.length];
}

function displayName(user: string | undefined) {
    return user || DEFAULT_USER_NAME;
}

function initialsFor(user: string | undefined) {
    const name = displayName(user);
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "AE";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

interface ChangeRowProps {
    change: FieldChange;
}

const ChangeRow = ({ change }: ChangeRowProps) => {
    return (
        <div className="flex items-center gap-1.5 px-2 py-1 border-b border-ae-gray-300 last:border-0 flex-wrap">
            <div className="flex min-w-[56px] flex-wrap items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-ae-slate-600">
                {change.formName && (
                    <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-xs font-black tracking-tight text-ae-slate-700">
                        {change.formName}
                    </span>
                )}
                <span className="break-all">
                    {change.field.replaceAll("_", " ")}
                </span>
            </div>
            <div className="flex items-center gap-1 flex-wrap">
                <span className="font-mono text-[11px] px-1 py-0.5 rounded bg-red-50 text-red-500 line-through">
                    {change.from}
                </span>
                {change.from !== "" &&
                    change.from !== null &&
                    change.to !== "" &&
                    change.to !== null && (
                        <FontAwesomeIcon
                            icon={faArrowRight}
                            className="text-ae-slate-600 shrink-0 text-[11px]"
                        />
                    )}
                <span className="font-mono text-[11px] px-1 py-0.5 rounded bg-emerald-50 text-emerald-600 font-medium">
                    {change.to}
                </span>
            </div>
        </div>
    );
};

interface HeaderProps {
    entry: HistoryEntry;
}

const Header = ({ entry }: HeaderProps) => {
    return (
        <div className="flex items-baseline gap-2 min-w-0">
            <span className="text-sm font-medium text-ae-slate-900 truncate">
                {displayName(entry.user)}
            </span>
            <span className="text-xs font-medium text-ae-slate-900 shrink-0">
                {formatDateTime(entry.modifiedAt)}
            </span>
        </div>
    );
};

interface TimelineEntryProps {
    entry: HistoryEntry;
    isLast: boolean;
}

const TimelineEntry = ({ entry, isLast }: TimelineEntryProps) => {
    const initials = initialsFor(entry.user);
    return (
        <div className="flex gap-2">
            {/* Rail */}
            <div className="flex flex-col items-center">
                <div
                    className={`w-[22px] h-[22px] rounded-full flex items-center justify-center text-[8px] font-bold text-white shrink-0 ${avatarColor(initials)}`}
                >
                    {initials}
                </div>
                {!isLast && (
                    <div className="w-px flex-1 bg-ae-gray-300 mt-0.5" />
                )}
            </div>

            {/* Content */}
            <div className="flex-1 pb-2">
                <Disclosure as="div" defaultOpen>
                    {({ open }) => (
                        <>
                            <Disclosure.Button className="w-full flex items-center justify-between gap-1.5 text-left">
                                <Header entry={entry} />
                                <div className="flex items-center gap-1 shrink-0">
                                    <span className="text-[11px] text-ae-slate-600">
                                        {entry.changes.length} change
                                        {entry.changes.length !== 1 ? "s" : ""}
                                    </span>
                                    <FontAwesomeIcon
                                        icon={faChevronDown}
                                        className={`text-ae-slate-600 text-[11px] transition-transform duration-150 ${open ? "rotate-180" : ""}`}
                                    />
                                </div>
                            </Disclosure.Button>
                            <Disclosure.Panel className="mt-1 rounded-md border border-ae-gray-300 bg-white overflow-hidden">
                                {entry.changeReason && (
                                    <div className="px-2 py-1 border-b border-ae-gray-300 text-[11px] text-ae-slate-900 italic">
                                        {entry.changeReason}
                                    </div>
                                )}
                                {/* Labels repeat, so they aren't unique keys */}
                                {entry.changes.map((c, i) => (
                                    <ChangeRow
                                        key={`${c.formName ?? ""}:${c.field}:${i}`}
                                        change={c}
                                    />
                                ))}
                            </Disclosure.Panel>
                        </>
                    )}
                </Disclosure>
            </div>
        </div>
    );
};

interface HistoryTimelineProps {
    history: HistoryEntry[];
}

const HistoryTimeline = ({ history }: HistoryTimelineProps) => {
    if (history.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-10 text-ae-slate-600 gap-1.5">
                <FontAwesomeIcon
                    icon={faClockRotateLeft}
                    className="text-2xl opacity-40"
                />
                <span className="text-xs">No changes recorded yet</span>
            </div>
        );
    }

    return (
        <div className="py-2 flex flex-col">
            {history.map((entry, i) => (
                <TimelineEntry
                    key={entry.modifiedAt.toISOString()}
                    entry={entry}
                    isLast={i === history.length - 1}
                />
            ))}
        </div>
    );
};

interface EventTimelineProps {
    eventId: string;
}

export const EventTimeline = ({ eventId }: EventTimelineProps) => {
    const apiClient = useEmissionEventsApiClient();
    const historyQuery = useQuery({
        queryKey: ["event", eventId, "history"],
        queryFn: () => apiClient.emissioneventsHistoryList({ id: eventId }),
        refetchOnWindowFocus: false,
        select: (data) =>
            data.map((entry) => ({
                ...entry,
                changes: [...entry.changes].sort((a, b) => {
                    const aKey = `${a.formName ?? ""}${a.field}`;
                    const bKey = `${b.formName ?? ""}${b.field}`;
                    return aKey.localeCompare(bKey);
                }),
            })),
    });

    if (historyQuery.isLoading || !historyQuery.data) {
        return (
            <div className="flex items-center justify-center gap-4 h-44">
                <FontAwesomeIcon
                    icon={faSpinner}
                    className="animate-spin h-24"
                />
                Loading...
            </div>
        );
    }

    if (historyQuery.isError) {
        return <div className="text-red-600">Failed to load history.</div>;
    }

    return <HistoryTimeline history={historyQuery.data} />;
};
